import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutlined as ApproveIcon, VerifiedOutlined as ConfirmedIcon } from '@mui/icons-material';
import DialogHeader from '../common/DialogHeader';
import { approvePickupOrder } from '../../services/order-approval.service';
import { formatKg, formatWhen } from '../../utils/formatters';

export interface ApproveOrderTarget {
  pickupOrderId: string;
  pointName: string | null;
  supplierName: string | null;
  weightKg: number | null;
  /** The collection point's QR handshake, when one exists. */
  confirmation: { status: string; name: string | null; respondedAt?: string | null } | null;
}

interface Props {
  open: boolean;
  organizationId: string;
  target: ApproveOrderTarget | null;
  onClose: () => void;
  onApproved: () => void;
}

/**
 * The facility manager's approval. A weight the collection point signed for is shown
 * as confirmed and locked; an unconfirmed one can be corrected before approving.
 */
export default function ApproveOrderDialog({ open, organizationId, target, onClose, onApproved }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const [weight, setWeight] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmed = target?.confirmation?.status === 'approved';

  useEffect(() => {
    if (open) {
      setWeight(target?.weightKg != null ? String(target.weightKg) : '');
      setError(null);
    }
  }, [open, target]);

  const submit = async () => {
    if (!target) return;
    const parsed = parseFloat(weight.replace(',', '.'));
    const changed = !confirmed && weight.trim() !== '' && Number.isFinite(parsed) && parsed !== target.weightKg;
    if (!confirmed && weight.trim() !== '' && (!Number.isFinite(parsed) || parsed <= 0)) {
      setError(t('approveDialog.invalidWeight'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await approvePickupOrder(organizationId, target.pickupOrderId, changed ? parsed : undefined);
      onApproved();
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail ?? t('orderDetails.approveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogHeader title={t('approveDialog.title')} subtitle={target?.pointName ?? undefined} />
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          {target?.supplierName ? (
            <Typography variant="body2" color="text.secondary">{target.supplierName}</Typography>
          ) : null}
          {confirmed ? (
            <Alert icon={<ConfirmedIcon fontSize="inherit" />} severity="success">
              <Typography variant="body2" fontWeight={600}>{t('approveDialog.confirmedTitle')}</Typography>
              <Typography variant="body2">
                {formatKg(target?.weightKg, locale)}
                {target?.confirmation?.name ? ` · ${target.confirmation.name}` : ''}
                {target?.confirmation?.respondedAt ? ` · ${formatWhen(target.confirmation.respondedAt, locale)}` : ''}
              </Typography>
            </Alert>
          ) : (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" color="warning" label={t('approveDialog.unconfirmed')} />
              </Stack>
              <TextField
                label={t('approveDialog.weightLabel')}
                type="number"
                fullWidth
                value={weight}
                onChange={(event) => setWeight(event.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                helperText={t('approveDialog.weightHelp')}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Box>
          )}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>{t('transportation.routes.form.cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon />}
          disabled={busy}
          onClick={() => void submit()}
        >
          {t('orderDetails.approve')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
